import { useCallback, useEffect, useMemo } from 'react';
import { chainCommands, exitCode } from 'prosemirror-commands';
import { NodeType } from 'prosemirror-model';
import { createTable, findParentNode } from 'prosemirror-utils';

import { getMatchingOptionsInSortedOrder } from '@lyearn/core/utils/search';
import { EditorView, REACT_COMPONENT_CLASSNAME } from '@lyearn/lygo-core';

import {
  blocksAllowedInsideTab,
  blocksAllowedInsideTableCell,
  blocksAllowedInsideToggleContent,
} from '../../consts/nodes';
import {
  addList,
  addTextBlock,
  filterAllowedAddMenuBlocks,
  isCheckList,
  isTable,
  isTabs,
  isToggleList,
  selectionType,
  setSelection,
} from '../../utils';
import { appendBlockAtEnd, createNewBlock, removeSlash } from './commands';
import { MENU_ITEM_HEIGHT } from './consts';
import { blockType } from './types';
import { handleKeyDownTypes, menuItemClickMapTypes } from './types';
import { hideAddMenu } from './utils';

export const useAvailableBlocks = (addMenuBlocks: blockType[], match?: string) => {
  return useMemo(
    () => getMatchingOptionsInSortedOrder(addMenuBlocks, match, { keys: ['searchWords'] }),
    [addMenuBlocks, match],
  );
};

//TODO: explicitly return avl block for each condition
const FILTER_ADD_MENU_BLOCKS = [
  {
    condition: isTable,
    getBlocks: (view: EditorView) => filterAllowedAddMenuBlocks(view, blocksAllowedInsideTableCell),
  },
  {
    condition: isTabs,
    getBlocks: (view: EditorView) => filterAllowedAddMenuBlocks(view, blocksAllowedInsideTab),
  },
  {
    condition: isToggleList,
    getBlocks: (view: EditorView) =>
      filterAllowedAddMenuBlocks(view, blocksAllowedInsideToggleContent),
  },

  {
    condition: isCheckList,
    getBlocks: (view: EditorView) => filterAllowedAddMenuBlocks(view, blocksAllowedInsideTab),
  },
];

export const filterAddMenuBlocks = (view?: EditorView) => {
  let blocks: [] | null = null;
  if (view) {
    FILTER_ADD_MENU_BLOCKS.some(({ condition, getBlocks }) => {
      if (findParentNode((node) => condition(node, view.state.schema))(view.state.selection)) {
        blocks = getBlocks(view);
        return true;
      }
      return false;
    });
  }
  return blocks;
};

export const usePopperOptions = (offset: number[]) => {
  return useMemo(() => {
    return {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          name: 'flip',
          enabled: false,
        },
      ],
    };
  }, [offset]);
};

// If the current selection is the text selection, new insertion should be 2 pos away
// from the selection, else for the node selection, it should be 1 pos away.
// If the current selection is the All selection, it should be 0 pos away from the selection.
const INSERT_POS_MAP = new Map([
  ['TextSelection', 2],
  ['NodeSelection', 1],
  ['AllSelection', 0],
]);

const MENU_ITEM_CLICK_MAP: menuItemClickMapTypes[] = [
  {
    condition: (view, block) => {
      const nodes = view.state.config.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      return newNode.isTextblock;
    },
    callback: (view, block) => {
      addTextBlock(view, block);
    },
  },
  {
    condition: (view, block) => {
      const nodes = view.state.config.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      return newNode.isLeaf;
    },
    callback: (view, block) => {
      const nodes = view.state.config.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      const prevSelection = view.state.selection.to;
      chainCommands(exitCode, createNewBlock(newNode, block.attrs))(view.state, view.dispatch);
      const domElement = view.nodeDOM(prevSelection - 1);
      if (domElement?.classList?.contains(REACT_COMPONENT_CLASSNAME)) {
        const inputElement =
          domElement.querySelector('input') || domElement.querySelector('textarea');
        inputElement?.focus();
      }
    },
  },
  {
    condition: (_view, block) => {
      return block.name === 'table';
    },
    callback: (view, _block) => {
      const { schema, selection } = view.state;
      const initalSelectionPos = selection.from;
      const tableNodes = createTable(schema, 3, 3);
      const tr = view.state.tr.replaceSelectionWith(tableNodes).scrollIntoView();
      view.dispatch(tr);

      //moving selection from last cell to first cell
      setSelection(view, selectionType.TEXT, initalSelectionPos + 2);
    },
  },
  {
    condition: (_view, block) => {
      return block.name === 'toggle_list' || block.name === 'check_list';
    },
    callback: (view, block) => {
      const nodes = view.state.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      const initalSelectionPos = view.state.selection.from;
      view.dispatch(view.state.tr.replaceSelectionWith(newNode.createAndFill()));

      if (block.name === 'toggle_list') {
        //after creating a toggle_list_item, by default selection is set to toggle_content
        //moving selection to toggle_heading
        setSelection(view, selectionType.TEXT, initalSelectionPos + 3);
      } else if (block.name === 'check_list') {
        //moving selection to check_list_item
        setSelection(view, selectionType.TEXT, initalSelectionPos + 2);
      }
    },
  },
  {
    condition: (_view, block) => {
      return block.name === 'ly__ai_video';
    },
    callback: (view, block) => {
      const nodes = view.state.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      view.dispatch(
        view.state.tr.replaceSelectionWith(newNode.createAndFill(block.attrs)).scrollIntoView(),
      );
    },
  },
  {
    condition: (view, block) => {
      const nodes = view.state.config.schema.nodes;
      const newNode: NodeType = nodes[block.name];
      return !newNode.isTextblock && !newNode.isLeaf;
    },
    callback: (view, block) => {
      addList(view, block.name);
    },
  },
];

export const useMenuItemClick = (view: any, isMicroLearning: boolean) => {
  return useCallback(
    (block: blockType) => {
      if (isMicroLearning) {
        const nodes = view.state.config.schema.nodes;
        const newNode: NodeType = nodes[block.name];
        chainCommands(exitCode, appendBlockAtEnd(newNode, block.attrs))(view.state, view.dispatch);
      } else {
        removeSlash(view.state, view.dispatch);
        for (const menuItemClickObject of MENU_ITEM_CLICK_MAP) {
          if (menuItemClickObject.condition(view, block)) {
            menuItemClickObject.callback(view, block);
            break;
          }
        }
      }

      hideAddMenu(view);
    },
    [isMicroLearning, view],
  );
};

export const useHandleKeyDown: handleKeyDownTypes = (
  open,
  selectedMenuItemIndex,
  setSelectedMenuItemIndex,
  availableBlocks,
  onMenuItemClick,
  view,
  menuRef,
) => {
  useEffect(() => {
    // Key-press handling in the Add Menu. We need to add it here as
    // setting focus to Popover element would not let users type in
    // the editor while Add Menu is open.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (open) {
        switch (event.key) {
          case 'ArrowUp':
            // Move the menu selection up.
            let newUpSelectedMenuitem = selectedMenuItemIndex - 1;
            if (newUpSelectedMenuitem < 0) {
              newUpSelectedMenuitem = availableBlocks.length - 1;
            }
            setSelectedMenuItemIndex(newUpSelectedMenuitem);
            menuRef.current?.scrollTo(0, newUpSelectedMenuitem * MENU_ITEM_HEIGHT);
            break;
          case 'ArrowDown':
            // Move the menu selection down.
            let newDownSelectedMenuitem = selectedMenuItemIndex + 1;
            if (newDownSelectedMenuitem > availableBlocks.length - 1) {
              newDownSelectedMenuitem = 0;
            }
            setSelectedMenuItemIndex(newDownSelectedMenuitem);
            menuRef.current?.scrollTo(0, newDownSelectedMenuitem * MENU_ITEM_HEIGHT);
            break;
          case 'Enter': {
            // Select the menu item and add corresponding block.
            availableBlocks[selectedMenuItemIndex] &&
              onMenuItemClick(availableBlocks[selectedMenuItemIndex]);
            break;
          }
          case 'Escape': {
            // Close the Menu Popover.
            hideAddMenu(view);
            view.focus();
            break;
          }
        }
        if (['ArrowUp', 'ArrowDown', 'Escape', 'Enter'].includes(event.key)) {
          event.stopPropagation();
          event.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    open,
    selectedMenuItemIndex,
    availableBlocks,
    onMenuItemClick,
    view,
    setSelectedMenuItemIndex,
    menuRef,
  ]);
};
